// The in-memory API's B5-DECIDE people routes (services/api/src/routes/decide.ts): the company
// switches (managers only; Jev on OpenRouter and the board gate both off by default, the
// OpenRouter key from Keys & connections, none in the fake), the DEs' rubrics (managers see every
// DE's, others the DEs they supervise), a person's edit (always a new version that builds on the
// newest) and the logged answers. Acme's Riley holds one rubric so Setup › Decisions has a row.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { json, notFound, problem } from "./fake";
import type { FakeDe } from "./fake-data";

type Settings = OperationOutput<"decide.settings.get">;
type Rubric = OperationOutput<"decisionRubrics.edit">;
type Detail = OperationOutput<"decisionRubrics.get">;
type Decision = OperationOutput<"decisions.list">["data"][number];

export const FAKE_DECIDE_ROUTES = [
  "decide.settings.get",
  "decide.settings.update",
  "decisionRubrics.list",
  "decisionRubrics.get",
  "decisionRubrics.edit",
  "decisions.list",
] as const satisfies readonly RouteId[];

export const FAKE_JEV_MODEL = "typesafe/jev-1.13";
const DEFAULT_ACT = 0.7;
const DEFAULT_REVIEW = 0.35;
const ONE_LINE = /^[^\r\n]*$/;

function invalid(path: string, message: string): Response {
  return problem(422, "validation_failed", "Validation failed", `${path}: ${message}`, {
    errors: [{ path, code: "invalid", message }],
  });
}

function seedRubric(de: FakeDe, at: string, id: string): Rubric {
  return {
    id,
    membership_id: de.membership_id,
    agent_name: de.display_name,
    seat_id: de.seat_id,
    key: "inbox_triage",
    version: 1,
    title: "Inbox triage",
    focus: "Route to whoever must act on it first.",
    questions: [
      {
        key: "route",
        type: "choice",
        question: "Who should handle this message?",
        options: [
          {
            key: "recruiter",
            what: "A candidate writes about an application, interview or offer.",
            not_for: "Invoices or client contracts.",
            examples: ["Can we move my interview to Friday?"],
          },
          {
            key: "account_manager",
            what: "A client writes about an open role or a placed candidate.",
          },
          { key: "ignore", what: "Newsletters, automated notices and spam." },
        ],
      },
      {
        key: "urgent",
        type: "boolean",
        question: "Does it need an answer today?",
        options: [
          { key: "true", what: "A deadline today, or a candidate or client waiting." },
          { key: "false", what: "Anything that can wait for the weekly round." },
        ],
      },
    ],
    act_threshold: DEFAULT_ACT,
    review_threshold: DEFAULT_REVIEW,
    note: null,
    saved_by: { kind: "digital_employee", membership_id: de.membership_id },
    created_at: at,
    decisions: 0,
  };
}

export function createDecideHandlers(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  /** The Acme company id: its first DE gets the seeded rubric. */
  seedCompanyId: string;
}): Partial<Record<RouteId, Handler>> {
  const switches = new Map<string, { external: boolean; board_gate: boolean }>();
  /** Every version, oldest first. */
  const versions: Rubric[] = [];
  const decisions: Decision[] = [];
  let seq = 0;
  const newId = () => {
    seq += 1;
    return `0198d000-0000-7000-8000-${seq.toString(16).padStart(12, "0")}`;
  };
  const riley = deps.desOf(deps.seedCompanyId)[0];
  if (riley) versions.push(seedRubric(riley, "2026-10-20T09:00:00.000Z", newId()));

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  /** The DEs whose runs the caller may view: managers every one, others those they supervise. */
  const visibleDes = (p: Principal) => {
    const all = deps.desOf(p.company.id);
    if (p.manager) return all;
    const me = membershipOf(p);
    return all.filter((d) => d.supervisor.membership_id === me);
  };
  const visibleIds = (p: Principal) => new Set(visibleDes(p).map((d) => d.membership_id));
  const newest = (p: Principal) => {
    const ids = visibleIds(p);
    const byKey = new Map<string, Rubric>();
    for (const v of versions) {
      if (!ids.has(v.membership_id)) continue;
      const k = `${v.membership_id}:${v.key}`;
      const prev = byKey.get(k);
      if (!prev || v.version > prev.version) byKey.set(k, v);
    }
    return [...byKey.values()];
  };
  const withCounts = (r: Rubric): Rubric => ({
    ...r,
    decisions: decisions.filter((d) => d.rubric_id === r.id).length,
  });

  function settings(p: Principal): Settings {
    const s = switches.get(p.company.id) ?? { external: false, board_gate: false };
    const key = { present: false, last4: null };
    const localAvailable = true;
    return {
      ...s,
      openrouter_key: key,
      local_available: localAvailable,
      provider: s.external && key.present ? "jev" : localAvailable ? "local" : null,
      jev_model: FAKE_JEV_MODEL,
    };
  }

  function detail(p: Principal, id: string): Detail | null {
    const ids = visibleIds(p);
    const v = versions.find((x) => x.id === id && ids.has(x.membership_id));
    if (!v) return null;
    const all = versions.filter((x) => x.membership_id === v.membership_id && x.key === v.key);
    const current = all.reduce((a, b) => (b.version > a.version ? b : a));
    return {
      ...withCounts(v),
      versions: [...all]
        .sort((a, b) => b.version - a.version)
        .map((x) => ({
          id: x.id,
          version: x.version,
          note: x.note,
          saved_by: x.saved_by,
          created_at: x.created_at,
        })),
      current_id: current.id,
    };
  }

  return {
    "decide.settings.get": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(settings(principal));
    },
    "decide.settings.update": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as Record<string, unknown>;
      for (const k of Object.keys(b))
        if (k !== "external" && k !== "board_gate") return invalid(k, "unknown field");
      for (const k of ["external", "board_gate"] as const)
        if (b[k] !== undefined && typeof b[k] !== "boolean") return invalid(k, "true or false");
      const prev = switches.get(principal.company.id) ?? { external: false, board_gate: false };
      switches.set(principal.company.id, {
        external: (b.external as boolean | undefined) ?? prev.external,
        board_gate: (b.board_gate as boolean | undefined) ?? prev.board_gate,
      });
      return json(settings(principal));
    },
    "decisionRubrics.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const only = url.searchParams.get("membership_id");
      const data = newest(principal)
        .filter((r) => !only || r.membership_id === only)
        .sort((a, b) =>
          (a.agent_name ?? "") === (b.agent_name ?? "")
            ? a.key < b.key
              ? -1
              : 1
            : (a.agent_name ?? "") < (b.agent_name ?? "")
              ? -1
              : 1,
        )
        .map(withCounts);
      return json({ data });
    },
    "decisionRubrics.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const d = detail(principal, params.id ?? "");
      return d ? json(d) : notFound();
    },
    "decisionRubrics.edit": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const d = detail(principal, params.id ?? "");
      if (!d) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const title = typeof b.title === "string" ? b.title.trim() : "";
      if (!title || title.length > 120 || !ONE_LINE.test(title))
        return invalid("title", "one line, 1 to 120 characters");
      if (!Array.isArray(b.questions) || b.questions.length === 0)
        return invalid("questions", "at least one question");
      const act = b.act_threshold === undefined ? DEFAULT_ACT : Number(b.act_threshold);
      const review = b.review_threshold === undefined ? DEFAULT_REVIEW : Number(b.review_threshold);
      if (!(act > 0 && act <= 1)) return invalid("act_threshold", "above 0, at most 1");
      if (!(review > 0 && review < 1)) return invalid("review_threshold", "between 0 and 1");
      if (review >= act)
        return invalid("review_threshold", "review_threshold must be below act_threshold");
      const note = typeof b.note === "string" && b.note.trim() ? b.note.trim() : null;
      if (note && (note.length > 300 || !ONE_LINE.test(note)))
        return invalid("note", "one line, at most 300 characters");
      const current = versions.find((v) => v.id === d.current_id);
      if (!current) return notFound();
      const focus = typeof b.focus === "string" && b.focus.trim() ? b.focus.trim() : null;
      const next: Rubric = {
        ...current,
        id: newId(),
        version: current.version + 1,
        title,
        focus: b.focus === undefined ? current.focus : focus,
        questions: b.questions as Rubric["questions"],
        act_threshold: act,
        review_threshold: review,
        note,
        saved_by: { kind: "person", membership_id: membershipOf(principal) },
        created_at: deps.now().toISOString(),
        decisions: 0,
      };
      versions.push(next);
      return json(next, 201);
    },
    "decisions.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const ids = visibleIds(principal);
      const q = url.searchParams;
      const limit = Math.min(200, Math.max(1, Number(q.get("limit") ?? 50) || 50));
      const data = decisions
        .filter(
          (d) =>
            ids.has(d.membership_id) &&
            (!q.get("membership_id") || d.membership_id === q.get("membership_id")) &&
            (!q.get("run_id") || d.run_id === q.get("run_id")) &&
            (!q.get("rubric_id") || d.rubric_id === q.get("rubric_id")) &&
            (!q.get("band") || d.band === q.get("band")),
        )
        .slice(0, limit);
      return json({ data });
    },
  };
}
